// FieldEditorForm.jsx

import React from 'react';

// CSS
import './FieldModal.css';

// Components
import ChecklistTemplateItemsField from './components/ChecklistTemplateItemsField';
import DefaultValueField from './components/DefaultValueField';
import EnumOptionsField from './components/EnumOptionsField';
import FieldAllowMultipleField from './components/FieldAllowMultipleField';
import FieldDescriptionField from './components/FieldDescriptionField';
import FieldDisplayFieldsField from './components/FieldDisplayFieldsField';
import FieldDisplayInDefaultListsField from './components/FieldDisplayInDefaultListsField';
import FieldIncludeInQrPrintField from './components/FieldIncludeInQrPrintField';
import FieldLabelField from './components/FieldLabelField';
import FieldShowAccessField from './components/FieldShowAccessField';
import FieldShowAsProfileStackField from './components/FieldShowAsProfileStackField';
import FieldShowFilterField from './components/FieldShowFilterField';
import FieldTargetTypeField from './components/FieldTargetTypeField';
import FieldTypeField from './components/FieldTypeField';
import { CURRENCY_CODE_OPTIONS } from './constants';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import ThemeColorButton from 'components/themecolorbutton/ThemeColorButton';

/**
 * FieldEditorForm
 *
 * Shared field editor body for FieldModal and Studio embedded tabs.
 *
 * @returns {ReactNode} The rendered component.
 */
const FieldEditorForm = ({
  width = '100%',
  checklistItemsState,
  enumOptionsState,
  formState,
  isEditing = false,
  theme,
}) => {

  // ----- RENDER ----- //

  return (
  <ModalForm width={width}>
    <FieldTypeField
      disabled={isEditing}
      draftType={formState.draft.type}
      error={formState.errors.type}
      mergedTypeOptions={formState.mergedTypeOptions}
      theme={theme}
      onTypeChange={(type) => {
        formState.clearError('type');
        formState.patchDraft({ type });
      }}
    />

    <FieldLabelField
      error={formState.errors.label}
      theme={theme}
      value={formState.draft.label}
      onChange={(label) => {
        formState.clearError('label');
        formState.patchDraft({ label });
      }}
    />

    <FieldDescriptionField
      error={formState.errors.fieldDescription}
      theme={theme}
      value={formState.draft.fieldDescription}
      onChange={(fieldDescription) => formState.patchDraft({ fieldDescription })}
    />

    <FieldDisplayInDefaultListsField
      displayInDefaultLists={formState.draft.displayInDefaultLists === true}
      onDisplayInDefaultListsChange={(displayInDefaultLists) => {
        formState.patchDraft({ displayInDefaultLists });
      }}
    />

    <FieldIncludeInQrPrintField
      includeInQrPrint={formState.draft.includeInQrPrint === true}
      onIncludeInQrPrintChange={(includeInQrPrint) => {
        formState.patchDraft({ includeInQrPrint });
      }}
    />

    {formState.isNodeListType ? (
      <>
        <FieldTargetTypeField
          disabled={isEditing}
          draftTargetType={formState.draft.targetType}
          error={formState.errors.targetType}
          targetTypeOptions={formState.targetTypeOptions}
          theme={theme}
          onTargetTypeChange={(targetType) => {
            formState.clearError('targetType');
            formState.patchDraft({ targetType });
          }}
        />

        <FieldAllowMultipleField
          allowMultiple={formState.draft.allowMultiple !== false}
          onAllowMultipleChange={(allowMultiple) => {
            formState.patchDraft({
              allowMultiple,
              ...(allowMultiple !== false ? { showFilter: false } : {}),
            });
          }}
        />

        {formState.draft.allowMultiple === false ? (
          <FieldShowFilterField
            showFilter={formState.draft.showFilter === true}
            onShowFilterChange={(showFilter) => {
              formState.patchDraft({ showFilter });
            }}
          />
        ) : null}

        <FieldDisplayFieldsField
          displayFields={formState.draft.displayFields}
          targetType={formState.draft.targetType}
          onDisplayFieldsChange={(displayFields) => {
            formState.patchDraft({ displayFields });
          }}
        />
      </>
    ) : null}

    {formState.isPeopleType ? (
      <>
        <FieldShowAccessField
          showAccess={formState.draft.showAccess === true}
          onShowAccessChange={(showAccess) => {
            formState.patchDraft({ showAccess });
          }}
        />

        <FieldShowAsProfileStackField
          showAsProfileStack={formState.draft.showAsProfileStack === true}
          onShowAsProfileStackChange={(showAsProfileStack) => {
            formState.patchDraft({ showAsProfileStack });
          }}
        />
      </>
    ) : null}

    {formState.isOptionsType ? (
      <EnumOptionsField
        addLabel={formState.isMeasurementType ? 'Add Unit' : 'Add Option'}
        emptyCopy={formState.isMeasurementType ? 'No units yet.' : 'No options yet.'}
        label={formState.isMeasurementType ? 'Units' : 'Options'}
        optionValueDrafts={enumOptionsState.optionValueDrafts}
        optionsError={formState.errors.options}
        placeholder={formState.isMeasurementType ? 'e.g. ft' : 'Internal value'}
        selectedEnumOptions={enumOptionsState.selectedEnumOptions}
        setOptionValueDrafts={enumOptionsState.setOptionValueDrafts}
        showColors={!formState.isMeasurementType}
        showIcons={!formState.isMeasurementType}
        theme={theme}
        onOptionAdd={enumOptionsState.handleOptionAdd}
        onOptionColorChange={enumOptionsState.handleOptionColorChange}
        onOptionIconChange={enumOptionsState.handleOptionIconChange}
        onOptionMove={enumOptionsState.handleOptionMove}
        onOptionRemove={enumOptionsState.handleOptionRemove}
        onOptionValueCommit={enumOptionsState.handleOptionValueCommit}
        onOptionValueKeyDown={enumOptionsState.handleOptionValueKeyDown}
      />
    ) : null}

    {formState.isChecklistType && checklistItemsState ? (
      <ChecklistTemplateItemsField
        allowCustomItems={formState.draft.allowCustomItems === true}
        checklistItems={checklistItemsState.checklistItems}
        itemLabelDrafts={checklistItemsState.itemLabelDrafts}
        setItemLabelDrafts={checklistItemsState.setItemLabelDrafts}
        theme={theme}
        onAllowCustomItemsChange={(allowCustomItems) => {
          formState.patchDraft({ allowCustomItems });
        }}
        onItemAdd={checklistItemsState.handleChecklistItemAdd}
        onItemLabelCommit={checklistItemsState.handleChecklistItemLabelCommit}
        onItemMoveByDelta={checklistItemsState.handleChecklistItemMoveByDelta}
        onItemRemove={checklistItemsState.handleChecklistItemRemove}
      />
    ) : null}

    {formState.isMeasurementType ? (
      <ModalField>
        <ModalLabel label="Default Unit" />
        <ModalHint hint="Select the default unit for the field." />

        <SelectInput
          disabled={enumOptionsState.selectedEnumOptions.length === 0}
          error={!!formState.errors.defaultUnit}
          helperText={formState.errors.defaultUnit}
          value={formState.draft.defaultUnit || ''}
          options={enumOptionsState.selectedEnumOptions.map((option) => ({
            value: option.value,
            label: option.value,
          }))}
          onChange={(event) => {
            formState.clearError('defaultUnit');
            formState.patchDraft({ defaultUnit: event.target.value });
          }}
        />
      </ModalField>
    ) : null}

    {!formState.isDefaultUnsupported ? (
      <DefaultValueField
        draft={formState.draft}
        isEnumType={formState.isEnumType}
        isTagsType={formState.isTagsType}
        selectedEnumOptions={enumOptionsState.selectedEnumOptions}
        theme={theme}
        onDefaultChange={(nextDefault) => formState.patchDraft({ default: nextDefault })}
      />
    ) : null}

    {formState.isTemporalType ? (
      <ModalField>
        <ModalLabel label="Calendar Color" />
        <ModalHint hint="Named theme color used for this field's events on calendars. Each theme mode defines its own shade, so events adapt to light and dark." />

        <ThemeColorButton
          tokenId={formState.draft.calendarColor || ''}
          onChange={(calendarColor) => formState.patchDraft({ calendarColor })}
        />
      </ModalField>
    ) : null}

    {formState.draft.type === 'percentage' ? (
      <ModalField>
        <ModalLabel label="Snap To (%)" />
        <ModalHint hint="The increment value for snapping percentages." />

        <TextInput
          min={1}
          placeholder="5"
          step={1}
          type="number"
          value={formState.draft.snapTo ?? 5}
          onChange={(event) => {
            formState.patchDraft({ snapTo: event.target.value });
          }}
        />

        {formState.errors.snapTo ? (
          <div className="field-modal-error">{formState.errors.snapTo}</div>
        ) : null}
      </ModalField>
    ) : null}

    {formState.draft.type === 'money' ? (
      <ModalField>
        <ModalLabel label="Amount currency" />
        <ModalHint hint="Amounts for this Money field are stored and displayed in this currency." />

        <SelectInput
          error={!!formState.errors.currencyCode}
          helperText={formState.errors.currencyCode}
          value={formState.draft.currencyCode || 'USD'}
          options={(() => {
            const selected = String(formState.draft.currencyCode || 'USD').trim().toUpperCase();
            return CURRENCY_CODE_OPTIONS.some((option) => option.value === selected)
              ? CURRENCY_CODE_OPTIONS
              : [{ value: selected, label: selected }, ...CURRENCY_CODE_OPTIONS];
          })()}
          onChange={(event) => {
            formState.clearError('currencyCode');
            formState.patchDraft({ currencyCode: event.target.value });
          }}
        />
      </ModalField>
    ) : null}
  </ModalForm>
);
};

// Dev Tools
FieldEditorForm.displayName = 'FieldEditorForm';

export default FieldEditorForm;
